import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import {
  useDeviceConfigEntries,
  useDevicesList,
  useSolutionType,
  useSystemId,
} from '../../hooks/useReduxSelectors';
import {
  Typography,
  useTheme,
  Box,
  Tooltip,
  Badge,
  Divider,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  styled
} from '@mui/material'
import {
  ViewColumn as ViewColumnIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Cancel as CancelIcon
} from '@mui/icons-material'
import {
  DataGrid,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportCsv,
  ExportPrint,
  QuickFilter,
  QuickFilterControl,
  QuickFilterClear,
  QuickFilterTrigger,
  useGridApiRef
} from '@mui/x-data-grid';
import InfoIcon from '@mui/icons-material/Info';
import DetailedDeviceModal from '../overview/devices/DetailedDeviceModal';

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  width: 'min-content',
  height: 'min-content',
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? 'none' : 'auto',
  transition: theme.transitions.create(['opacity']),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity']),
}));

const DeviceListView = () => {

  const apiRef = useGridApiRef();
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDevice, setModalDevice] = useState(null);
  const [modalDeviceConfigs, setModalDeviceConfigs] = useState(null);

  const onClickOpenModal = (deviceId) => {
    const device = findDeviceById(deviceId);
    setModalDevice(device);
    const deviceConfig = findDeviceConfigsById(deviceId);
    setModalDeviceConfigs(deviceConfig);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setModalDevice(null);
    setModalDeviceConfigs(null);
  }

  const deviceConfigs = useDeviceConfigEntries();
  const devices = useDevicesList();
  // console.log("devices in DeviceListView:", devices);
  const solutionType = useSolutionType();
  const systemId = useSystemId();


  const findDeviceById = useCallback((id) => {
    console.log("id to find:", id);
    console.log("devices list:", devices);
    return devices.find(device => device.hwId === id);
  }, [devices]);

  const findDeviceConfigsById = useCallback((id) => {
    return deviceConfigs.find(device => device.hwId === id);
  }, [deviceConfigs]);

  useEffect(() => {
    console.log("deviceConfigs changed:", deviceConfigs);
  }, [deviceConfigs]);

  const theme = useTheme();


  const determineColor = (bgColor) => {
    switch (bgColor) {
      case 'red':
        return theme.palette.error.main;
      case 'orange':
        return theme.palette.warning.main;
      case 'green':
        return theme.palette.text.primary;
      default:
        return theme.palette.text.primary;
    }
  };

  const determineValueColor = (bgColor) => {
    switch (bgColor) {
      case 'red':
        return theme.palette.error.main;
      case 'orange':
        return theme.palette.warning.main;
      case 'green':
        return theme.palette.primary.main;
      default:
        return theme.palette.text.primary;
    }
  };

  const measurementColumns = useMemo(() => {
    const allMeasurements = new Set();

    if (deviceConfigs && deviceConfigs.length > 0) {
      deviceConfigs.forEach(config => {
        if (config.measurements && config.measurements.length > 0) {
          config.measurements.forEach(measurement => {
            allMeasurements.add(measurement.name); // Use 'name' field from deviceConfigs
          });
        }
      });
    }

    return Array.from(allMeasurements);
  }, [deviceConfigs]);

  // Create measurement map for a specific device - map by valueName to handle socket data
  const createMeasurementMap = useCallback((measurements) => {
    if (!measurements) return {};
    return measurements.reduce((acc, item) => {
      acc[item.valueName] = item; // Use valueName as key for socket data lookup
      return acc;
    }, {});
  }, []);

  // State for DataGrid
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});


  // Initial columns setup - static column definitions
  const initialColumns = useMemo(() => [
    {
      field: 'deviceName',
      headerName: 'Device Name',
      width: 200,
      //custom quickfilter because the cell has both name and tag in it
      getApplyQuickFilterFn: (value) => {
        if (!value || value.trim() === '') {
          return null;
        }
        const searchValue = value.toLowerCase();
        return (row, colDef, params) => {
          const deviceName = (colDef.deviceName || '').toString().toLowerCase();
          const deviceTag = (colDef.deviceTag || '').toString().toLowerCase();
          return deviceName.includes(searchValue) || deviceTag.includes(searchValue);
        };
      },
      renderCell: (params) => (
        <Box>
          <Typography
            variant="subtitle2"
            color={determineColor(params.row.deviceBgColor)}
          >
            {params.value}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.deviceTag}
          </Typography>
        </Box>
      ),
    },
    ...measurementColumns.map(columnName => ({
      field: columnName.replace(/\s+/g, '_'), // Replace spaces with underscores for field names
      headerName: columnName,
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        // console.log("params in renderCell:", params);
        const value = params.value;
        return value ? (
          <Typography
            variant="body2"
            color={params.row[`${params.field}_isAlarmActive`] ? determineValueColor(params.row.deviceBgColor) : 'default'}
          >
            {value}</Typography>
        ) : (
          <Typography variant="caption" color="textSecondary">-</Typography>
        );
      }
    })),
    {
      field: 'detailsDialog',
      headerName: 'All Details',
      width: 100,
      renderCell: (params) => (
        <Box>
          {/* {console.log("params in diagnosticsDialog renderCell:", params)} */}
          <Tooltip title="Open All Details">
            <InfoIcon style={{ cursor: 'pointer' }} onClick={() => onClickOpenModal(params.id)} />
          </Tooltip>
        </Box>
      ),
    },
  ], [measurementColumns]);


  // Imperative update of rows when data changes
  useEffect(() => {
    if (!deviceConfigs || !apiRef.current) {
      return;
    }

    try {
      const rowUpdates = deviceConfigs.map((deviceConfig) => {
        const device = devices?.find(d => d.hwId === deviceConfig.hwId);
        const measurementMap = device ? createMeasurementMap(device.measurements) : {};

        const row = {
          id: deviceConfig.hwId,
          deviceName: deviceConfig.name,
          deviceTag: deviceConfig.tag || '',
          deviceBgColor: device ? device.deviceBgColor : 'default',
        };

        // Add measurement values as columns
        measurementColumns.forEach(columnName => {
          const fieldName = columnName.replace(/\s+/g, '_');
          // row[`${fieldName}_isAlarmActive`] = alarmActive;

          // Find the config measurement that matches this column name
          const configMeasurement = deviceConfig.measurements?.find(m => m.name === columnName);

          if (configMeasurement && device) {
            // Use the valueName to lookup actual measurement data
            const measurement = measurementMap[configMeasurement.valueName];
            const isAlarmActive = measurement ? measurement.isActive : false;
            row[fieldName] = measurement ? `${measurement.value} ${measurement.unit || ''}` : null;
            row[`${fieldName}_isAlarmActive`] = isAlarmActive;

            // row[fieldName] = measurement ? {
            //   value: `${measurement.value} ${measurement.unit || ''}`,
            //   isAlarmActive: isAlarmActive
            // } : null;
          } else {
            row[fieldName] = null;
            row[`${fieldName}_isAlarmActive`] = false;
          }
        });

        return row;
      });

      //// Update all rows at once // pro version // this would have better performance
      // apiRef.current.updateRows(rowUpdates);

      // Update rows one by one for free DataGrid
      rowUpdates.forEach(row => {
        apiRef.current.updateRows([row]);
      });
      setLoading(false);
    } catch (err) {
      console.error('Failed to update device data:', err);
    }
  }, [deviceConfigs, devices, measurementColumns, createMeasurementMap, apiRef]);




  // const autoSize = useCallback(() => {
  //   apiRef.current?.autosizeColumns({
  //     includeHeaders: true,
  //     includeOutliers: true,
  //   });
  // }, [apiRef]);

  // ///// CUSTOM TOOLBAR /////
  // function CustomToolbar() {
  //   const [exportMenuOpen, setExportMenuOpen] = useState(false);
  //   const exportMenuTriggerRef = useRef(null);

  //   return (
  //     <Toolbar sx={{ justifyContent: "space-between" }} >
  //       <Box sx={{ display: "flex", alignItems: "center" }}>
  //         <Tooltip title="Columns">
  //           <ColumnsPanelTrigger render={<ToolbarButton />}>
  //             <ViewColumnIcon fontSize="small" />
  //           </ColumnsPanelTrigger>
  //         </Tooltip>

  //         <Tooltip title="Filters">
  //           <FilterPanelTrigger
  //             render={(props, state) => (
  //               <ToolbarButton {...props} color="default">
  //                 <Badge badgeContent={state.filterCount} color="primary" variant="dot">
  //                   <FilterListIcon fontSize="small" />
  //                 </Badge>
  //               </ToolbarButton>
  //             )}
  //           />
  //         </Tooltip>

  //         <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
  //         <Tooltip title="Export">
  //           <ToolbarButton
  //             ref={exportMenuTriggerRef}
  //             id="export-menu-trigger"
  //             aria-controls="export-menu"
  //             aria-haspopup="true"
  //             aria-expanded={exportMenuOpen ? 'true' : undefined}
  //             onClick={() => setExportMenuOpen(true)}
  //           >
  //             <FileDownloadIcon fontSize="small" />
  //           </ToolbarButton>
  //         </Tooltip>

  //         <Menu
  //           id="export-menu"
  //           anchorEl={exportMenuTriggerRef.current}
  //           open={exportMenuOpen}
  //           onClose={() => setExportMenuOpen(false)}
  //           anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
  //           transformOrigin={{ vertical: 'top', horizontal: 'right' }}
  //           slotProps={{
  //             list: {
  //               'aria-labelledby': 'export-menu-trigger',
  //             },
  //           }}
  //         >
  //           <ExportPrint render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
  //             Print
  //           </ExportPrint>
  //           <ExportCsv render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
  //             Download as CSV
  //           </ExportCsv>
  //         </Menu>

  //         <StyledQuickFilter>
  //           <QuickFilterTrigger
  //             render={(triggerProps, state) => (
  //               <Tooltip title="Search" enterDelay={0}>
  //                 <StyledToolbarButton
  //                   {...triggerProps}
  //                   ownerState={{ expanded: state.expanded }}
  //                   color="default"
  //                   aria-disabled={state.expanded}
  //                 >
  //                   <SearchIcon fontSize="small" />
  //                 </StyledToolbarButton>
  //               </Tooltip>
  //             )}
  //           />
  //           <QuickFilterControl
  //             render={({ ref, ...controlProps }, state) => (
  //               <StyledTextField
  //                 {...controlProps}
  //                 ownerState={{ expanded: state.expanded }}
  //                 inputRef={ref}
  //                 aria-label="Search"
  //                 placeholder="Search..."
  //                 size="small"
  //                 slotProps={{
  //                   input: {
  //                     startAdornment: (
  //                       <InputAdornment position="start">
  //                         <SearchIcon fontSize="small" />
  //                       </InputAdornment>
  //                     ),
  //                     endAdornment: state.value ? (
  //                       <InputAdornment position="end">
  //                         <QuickFilterClear
  //                           edge="end"
  //                           size="small"
  //                           aria-label="Clear search"
  //                           material={{ sx: { marginRight: -0.75 } }}
  //                         >
  //                           <CancelIcon fontSize="small" />
  //                         </QuickFilterClear>
  //                       </InputAdornment>
  //                     ) : null,
  //                     ...controlProps.slotProps?.input,
  //                   },
  //                   ...controlProps.slotProps,
  //                 }}
  //               />
  //             )}
  //           />
  //         </StyledQuickFilter>
  //       </Box>
  //     </Toolbar >
  //   );
  // }
  // ///// END OF CUSTOM TOOLBAR /////


  return (
    <>
      <DataGrid
        apiRef={apiRef}
        density="compact"
        columns={initialColumns}
        loading={loading}
        checkboxSelection={false}
        disableRowSelectionOnClick
        // slots={{ toolbar: CustomToolbar }}
        showToolbar
        getRowHeight={() => 'auto'}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) =>
          setColumnVisibilityModel(newModel)
        }
      />
      <DetailedDeviceModal
        show={isModalOpen}
        onHide={onCloseModal}
        title={modalDeviceConfigs ? modalDeviceConfigs.name : ''}
        diagnostics={modalDevice ? modalDevice.diagnostics : []}
        extraDiagnostics={modalDevice ? modalDevice.extraDiagnostics : []}
        measurements={modalDevice ? modalDevice.measurements : []}
        color={modalDevice ? modalDevice.deviceBgColor : ''}
        systemId={systemId}
        hwId={modalDevice ? modalDevice.hwId : ''}
        isLora={solutionType === "lora"}
      />
    </>


  )
}

export default DeviceListView
